import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Input, Select, Typography, Upload } from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import CustomDrawer from "../../../../common/custom-drawer/custom-drawer";
import CustomButton from "../../../../common/custom-button/custom-button";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import styles from "../details-card/details-card.module.css";
import { createAnnouncement, updateAnnouncement } from "../../../../services/announcement";

const { Option } = Select;
const { TextArea } = Input;

const AnnouncementDrawer = ({
  visible,
  onClose,
  mode = "add",
  initialData = null,
  setReload,
  allLevels,
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      announcement_for: [],
      levels: [],
      title: "",
      description: "",
      file: null,
    },
  });

  const AnnouncementOptions = [
    {
      label: "Teachers",
      value: "TEACHER",
    },
    {
      label: "Parents",
      value: "PARENT",
    },
    {
      label: "Students",
      value: "STUDENT",
    },
  ];

  useEffect(() => {
    if (visible && mode === "edit" && initialData) {
      const levelsValue = Array.isArray(initialData.levels)
        ? initialData.levels.map((level) =>
            typeof level === "object" && level.id ? level.id : level
          )
        : initialData.levels || [];

      reset({
        announcement_for:
          initialData.announcement_for.split(",").map((item) => item.trim()) ||
          [],
        levels: levelsValue,
        title: initialData.title || "",
        description: initialData.description || "",
        file: null,
      });

      if (initialData.file_url) {
        const urlParts = initialData.file_url.split("/");
        const filename = urlParts[urlParts.length - 1]
          .split("%2F")
          .pop()
          .split("#")
          .pop();

        const fileObject = {
          uid: initialData.id,
          name: filename,
          status: "done",
          url: initialData.file_url,
          type: initialData.file_mimetype || "application/octet-stream",
        };

        setUploadedFile(fileObject);
      } else {
        setUploadedFile(null);
      }
    } else if (visible && mode === "add") {
      reset({
        announcement_for: [],
        levels: [],
        title: "",
        description: "",
        file: null,
      });
      setUploadedFile(null);
    }
  }, [visible, mode, initialData, reset]);

  const handleClose = () => {
    reset();
    setUploadedFile(null);
    onClose();
  };

  const handleFormSubmit = async (data) => {
    setLoading(true);
    try {
      const formData = new FormData();

      let announcementForValue;
      if (Array.isArray(data.announcement_for)) {
        if (
          data.announcement_for.includes("ALL") ||
          (data.announcement_for.includes("TEACHER") &&
            data.announcement_for.includes("PARENT") &&
            data.announcement_for.includes("STUDENT"))
        ) {
          announcementForValue = "ALL";
        } else {
          announcementForValue = data.announcement_for.join(",");
        }
      } else {
        announcementForValue = data.announcement_for;
      }

      formData.append("announcement_for", announcementForValue);
      formData.append("levels", JSON.stringify(data.levels));
      formData.append("title", data.title);
      formData.append("description", data.description || "");

      if (uploadedFile && uploadedFile instanceof File) {
        formData.append("file", uploadedFile);
      }

      if (mode === "edit" && initialData?.id) {
        formData.append("id", initialData.id);
      }

      let res;
      if (mode === "add") {
        res = await createAnnouncement(formData);
      } else {
        res = await updateAnnouncement(initialData?.id, formData);
      }

      if (res?.data?.status === true) {
        toast.success(
          `Announcement ${mode === "add" ? "created" : "updated"} successfully!`
        );
        setReload((prev) => !prev);
      }
      handleClose();
    } catch (error) {
      console.error(
        `Error ${mode === "add" ? "creating" : "updating"} announcement:`,
        error
      );
      toast.error(
        `Failed to ${mode === "add" ? "create" : "update"} Announcement!`
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    beforeUpload: (file) => {
      const isValidSize = file.size / 1024 / 1024 < 5; // 5MB limit
      if (!isValidSize) {
        toast.error("File must be smaller than 5MB!");
        return false;
      }
      setUploadedFile(file);
      setValue("file", file);
      return false;
    },
    onRemove: () => {
      setUploadedFile(null);
      setValue("file", null);
    },
    fileList: uploadedFile ? [uploadedFile] : [],
    onDownload: (file) => {
      if (file.url) {
        window.open(file.url, "_blank");
      }
    },
    showUploadList: {
      showDownloadIcon: true,
      showRemoveIcon: true,
    },
  };

  const handleSelectAll = (value) => {
    if (value?.includes("all")) {
      if (value.length === allLevels.length + 1) {
        return [];
      }

      return allLevels.map((item) => item.id);
    }
    return value;
  };

  const drawerTitle =
    mode === "add" ? "Create New Announcement" : "Edit Announcement";
  const submitButtonText =
    mode === "add" ? "Create Announcement" : "Update Announcement";

  return (
    <CustomDrawer
      title={drawerTitle}
      width={500}
      onClose={handleClose}
      open={visible}
      className={styles.drawer}
      destroyOnClose
      extra={
        <CustomButton
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
        />
      }
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <CustomButton
            variant="secondary"
            title={<Typography>Cancel</Typography>}
            onClick={handleClose}
            disabled={loading}
            style={{ backgroundColor: "white" }}
          />
          <CustomButton
            title={submitButtonText}
            type="primary"
            onClick={handleSubmit(handleFormSubmit)}
            loading={loading}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Announcement For<span style={{ color: "red" }}>*</span>
            {errors.announcement_for && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </label>
          <Controller
            name="announcement_for"
            control={control}
            rules={{ required: "Please select announcement type" }}
            render={({ field }) => (
              <CustomSelect
                {...field}
                mode="multiple"
                placeholder="Announcement for"
                allowClear
                style={{ width: "100%" }}
              >
                {AnnouncementOptions?.map((options) => (
                  <Option key={options?.value} value={options?.value}>
                    {options?.label}
                  </Option>
                ))}
              </CustomSelect>
            )}
          />
          {errors?.announcement_for && (
            <div
              style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
            >
              {errors?.announcement_for?.message}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Select Level<span style={{ color: "red" }}>*</span>
            {errors.levels && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </label>
          <Controller
            name="levels"
            control={control}
            rules={{
              required: "Please select levels",
              validate: (value) =>
                value.length > 0 || "Please select at least one levels",
            }}
            render={({ field }) => (
              <Select
                {...field}
                mode="multiple"
                placeholder="Select levels"
                allowClear
                style={{ width: "100%" }}
                onChange={(value) => field.onChange(handleSelectAll(value))}
              >
                <Option key="all" value="all">
                  ---SELECT ALL---
                </Option>
                {allLevels?.map((item) => (
                  <Option value={item.id}>{item.name}</Option>
                ))}
              </Select>
            )}
          />
          {errors.levels && (
            <div
              style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
            >
              {errors.levels.message}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Announcement Title<span style={{ color: "red" }}>*</span>
            {errors.title && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </label>
          <Controller
            name="title"
            control={control}
            rules={{
              required: "Please enter announcement title",
              minLength: {
                value: 3,
                message: "Title must be at least 3 characters",
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter announcement title"
                status={errors.title ? "error" : ""}
              />
            )}
          />
          {errors.title && (
            <div
              style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
            >
              {errors.title.message}
            </div>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Description (Optional)
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                rows={4}
                placeholder="Type description here"
                showCount
                maxLength={2000}
              />
            )}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Upload File (Optional)
          </label>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <Upload {...uploadProps} maxCount={1}>
                <div
                  style={{
                    border: "2px dashed #d9d9d9",
                    borderRadius: "6px",
                    padding: "40px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color 0.3s",
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = "#1890ff";
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                >
                  <UploadOutlined
                    style={{
                      fontSize: "24px",
                      color: "#1890ff",
                      marginBottom: "8px",
                    }}
                  />
                  <div>Click to Upload or drag and drop</div>
                  <div
                    style={{
                      color: "#999",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    Max file size: 5 MB
                  </div>
                </div>
              </Upload>
            )}
          />
        </div>
      </form>
    </CustomDrawer>
  );
};

export default AnnouncementDrawer;
